<template>
  <dashboard-layout title="Horoscope Podcast">
    <template #actions>
      <label class="date-field">From <input type="date" v-model="startDate" :max="endDate" /></label>
      <label class="date-field">To <input type="date" v-model="endDate" :min="startDate" /></label>
      <div class="quick-range">
        <button @click="setRange(7)">7d</button>
        <button @click="setRange(30)">30d</button>
        <button @click="setRange(90)">90d</button>
      </div>
      <button class="refresh-btn" @click="loadData" :disabled="loading">{{ loading ? 'Loading…' : 'Apply' }}</button>
    </template>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>
    <div v-if="truncated" class="error-banner">
      Showing the first {{ items.length.toLocaleString('en-US') }} generations for this range — narrow the range to see the rest.
    </div>

    <loading-state v-if="loading && !items.length" label="Loading podcasts…" />

    <template v-else>
      <!-- Summary cards -->
      <stat-grid>
        <stat-card label="Podcasts generated" :value="items.length.toLocaleString('en-US')" icon="mic" accent>
          <template #sub>{{ stats.distinctUsers }} user{{ stats.distinctUsers === 1 ? '' : 's' }} · {{ rangeLabel }}</template>
        </stat-card>
        <stat-card label="ElevenLabs cost (est.)" :value="formatCost(stats.totalCost)" icon="dollar">
          <template #sub>avg {{ formatCost(stats.avgCost) }} / podcast</template>
        </stat-card>
        <stat-card label="Avg total time" :value="formatMs(stats.avgTotalMs)" icon="activity">
          <template #sub>transit {{ formatMs(stats.avgTransitMs) }} · text {{ formatMs(stats.avgTextGenMs) }} · audio {{ formatMs(stats.avgAudioSynthMs) }}</template>
        </stat-card>
        <stat-card label="Characters billed" :value="formatTokens(stats.totalChars)" icon="filetext">
          <template #sub>{{ stats.totalChars.toLocaleString('en-US') }} chars · avg {{ Math.round(stats.avgChars).toLocaleString('en-US') }}</template>
        </stat-card>
        <stat-card label="Engine split" :value="stats.dialoguePct + '%'" icon="sliders">
          <template #sub>dialogue · {{ 100 - stats.dialoguePct }}% per-turn</template>
        </stat-card>
      </stat-grid>

      <p class="note">
        Cost is an estimate at <strong>{{ rateLabel }}</strong> per 1k chars (<code>ttsRatePer1kChars</code>).
        Tester/TestFlight users are <strong>not</strong> excluded — no filter is applied elsewhere in the app.
      </p>

      <empty-state
        v-if="!items.length"
        title="No podcasts generated in this range."
        icon="mic"
      >
        Rows accrue as the deployed <code>getHoroscopePodcast</code> is called. Try widening the date range.
      </empty-state>

      <template v-else>
        <!-- Cost per day -->
        <data-panel title="Cost per day (USD)">
          <div class="chart-box"><canvas ref="costCanvas"></canvas></div>
        </data-panel>

        <!-- Podcasts per day + Avg audio synthesis time -->
        <div class="dual-grid">
          <data-panel title="Podcasts per day">
            <div class="chart-box small"><canvas ref="countCanvas"></canvas></div>
          </data-panel>
          <data-panel title="Avg audio synthesis time (ElevenLabs)">
            <div class="chart-box small"><canvas ref="synthCanvas"></canvas></div>
          </data-panel>
        </div>

        <!-- Filters -->
        <div class="filters">
          <filter-chips v-model="engineFilter" :options="engineOptions" />
          <search-box v-model="searchQuery" placeholder="Search user / reading / date…" />
          <div class="order-box">
            <select v-model="sortKey">
              <option value="date-desc">Newest</option>
              <option value="date-asc">Oldest</option>
              <option value="cost-desc">Highest cost</option>
              <option value="synth-desc">Slowest synth</option>
              <option value="total-desc">Slowest total</option>
            </select>
          </div>
        </div>

        <!-- Detail table -->
        <data-panel flush>
          <template #title>Recent generations <span class="muted">({{ filteredItems.length.toLocaleString('en-US') }})</span></template>
          <data-table
            :columns="columns"
            :rows="filteredItems"
            row-key="id"
            clickable
            :expanded-key="expandedId"
            @row-click="toggleRow"
          >
            <template #cell-created="{ row }">
              <span class="date-cell">
                <span class="expand-caret" :class="{ open: expandedId === row.id }">▸</span>
                {{ formatDate(row.timestampMs) }}
              </span>
            </template>
            <template #cell-user="{ row }">
              <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link" @click.stop.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
              <span v-else class="muted">—</span>
            </template>
            <template #cell-forDate="{ row }">{{ row.date || '—' }}</template>
            <template #cell-engine="{ row }">
              <status-badge :variant="row.audioEngine === 'dialogue' ? 'primary' : 'warning'" :label="engineLabel(row.audioEngine)" />
            </template>
            <template #cell-chars="{ row }">{{ row.audioChars.toLocaleString('en-US') }}</template>
            <template #cell-cost="{ row }"><span class="strong">{{ formatCost(row.ttsCostUsd) }}</span></template>
            <template #cell-synth="{ row }">{{ formatMs(row.audioSynthMs) }}</template>
            <template #cell-total="{ row }">{{ formatMs(row.totalMs) }}</template>
            <template #cell-transits="{ row }">{{ row.usedTransits ? '✓' : '—' }}</template>
            <template #cell-chatTheme="{ row }">{{ row.usedChatTheme ? '✓' : '—' }}</template>

            <template #expanded="{ row }">
              <div class="reading-detail">
                <div v-if="detailState(row).loading" class="rd-msg">Loading reading…</div>
                <div v-else-if="detailState(row).error" class="rd-msg rd-error">{{ detailState(row).error }}</div>
                <template v-else>
                  <div class="rd-head">
                    <div class="rd-titles">
                      <div class="rd-title">{{ detailState(row).title || '—' }}</div>
                      <div v-if="detailState(row).subtitle" class="rd-sub">{{ detailState(row).subtitle }}</div>
                    </div>
                    <audio
                      v-if="detailState(row).audioUrl"
                      controls
                      preload="none"
                      :src="detailState(row).audioUrl"
                      class="rd-audio"
                    ></audio>
                    <span v-else class="muted">no audio</span>
                  </div>

                  <p v-if="detailState(row).summary" class="rd-summary">{{ detailState(row).summary }}</p>

                  <div v-if="detailState(row).dialogueTurns.length" class="rd-chat">
                    <div v-for="(t, ti) in detailState(row).dialogueTurns" :key="ti" class="bubble-row" :class="'sp-' + (t.speaker || 'host')">
                      <div class="avatar">{{ (t.speaker || 'h').charAt(0).toUpperCase() }}</div>
                      <div class="bubble">
                        <div class="bubble-meta">
                          <span class="sp-name">{{ t.speaker || 'host' }}</span>
                          <span v-if="parseTurn(t.text).emotion" class="emotion">{{ parseTurn(t.text).emotion }}</span>
                        </div>
                        <div class="bubble-text">{{ parseTurn(t.text).text }}</div>
                      </div>
                    </div>
                  </div>

                  <div v-if="detailState(row).suggestedQuestions.length" class="rd-qs">
                    <div class="rd-qs-title">Suggested questions</div>
                    <div class="q-chips">
                      <span v-for="(q, qi) in detailState(row).suggestedQuestions" :key="qi" class="q-chip">{{ q }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </template>

            <template #empty>No generations match your filters</template>
          </data-table>
        </data-panel>
      </template>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Chart } from 'chart.js/auto'
import { FirebaseReadingGenerationsService } from '../services/firebaseReadingGenerationsService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const ENGINE_LABELS = { 'dialogue': 'Dialogue (v3)', 'per-turn': 'Per-turn' }

const columns = [
  { key: 'created', label: 'Created', width: '150px' },
  { key: 'user', label: 'User', width: '120px' },
  { key: 'forDate', label: 'For date' },
  { key: 'engine', label: 'Engine' },
  { key: 'chars', label: 'Chars' },
  { key: 'cost', label: 'Cost' },
  { key: 'synth', label: 'Synth' },
  { key: 'total', label: 'Total' },
  { key: 'transits', label: 'Transits' },
  { key: 'chatTheme', label: 'Chat theme' }
]

function ymdIstanbul(d) {
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' })
}

export default {
  name: 'HoroscopePodcastDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, EmptyState, StatusBadge
  },
  setup() {
    const router = useRouter()
    const service = new FirebaseReadingGenerationsService()

    // Accordion: one expanded row at a time (keyed by the generation doc id).
    // The reading detail (audio + script) lives in readings/{readingId} and is
    // fetched lazily on first expand, cached by readingId.
    const EMPTY_DETAIL = { loading: false, error: '', loaded: false, audioUrl: '', title: '', subtitle: '', summary: '', dialogueTurns: [], suggestedQuestions: [] }
    const expandedId = ref(null)
    const detailCache = reactive({})
    const detailState = (row) => detailCache[row.readingId] || EMPTY_DETAIL

    // Split a "[emotion] spoken text" turn into its emotion tag + text.
    const parseTurn = (text) => {
      if (!text) return { emotion: '', text: '' }
      const m = text.match(/^\s*\[([^\]]+)\]\s*([\s\S]*)$/)
      return m ? { emotion: m[1], text: m[2] } : { emotion: '', text }
    }

    const toggleRow = async (row) => {
      if (expandedId.value === row.id) { expandedId.value = null; return }
      const rid = row.readingId
      if (!detailCache[rid] || !detailCache[rid].loaded) {
        detailCache[rid] = { ...EMPTY_DETAIL, loading: true }
      }
      expandedId.value = row.id
      if (detailCache[rid] && detailCache[rid].loaded) return
      try {
        const res = rid ? await service.getReadingDetail(rid) : null
        if (res) {
          detailCache[rid] = { loading: false, error: '', loaded: true, ...res }
        } else {
          detailCache[rid] = { ...EMPTY_DETAIL, loaded: true, error: 'Reading not found (no audio/script stored).' }
        }
      } catch (e) {
        detailCache[rid] = { ...EMPTY_DETAIL, loaded: true, error: (e && e.message) || 'Failed to load reading.' }
      }
    }

    const today = new Date()
    const endDate = ref(ymdIstanbul(today))
    const startDate = ref(ymdIstanbul(new Date(today.getTime() - 29 * 86400000)))

    const loading = ref(false)
    const error = ref('')
    const truncated = ref(false)
    const items = ref([])

    const searchQuery = ref('')
    const engineFilter = ref('all')
    const sortKey = ref('date-desc')

    const costCanvas = ref(null)
    const countCanvas = ref(null)
    const synthCanvas = ref(null)
    let costChart = null
    let countChart = null
    let synthChart = null

    const setRange = days => {
      const now = new Date()
      endDate.value = ymdIstanbul(now)
      startDate.value = ymdIstanbul(new Date(now.getTime() - (days - 1) * 86400000))
      loadData()
    }

    const loadData = async () => {
      loading.value = true
      error.value = ''
      truncated.value = false
      try {
        const res = await service.getByDateRange({ startDate: startDate.value, endDate: endDate.value })
        items.value = res.items
        truncated.value = res.truncated
        // Charts are redrawn by the flush:'post' watch on `items` below, after the
        // DOM (incl. the panels that first mount when data arrives) is patched.
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') {
          error.value = 'Firestore denied the read (not signed in). Reload the page and try again.'
        } else if (code === 'failed-precondition') {
          error.value = (e && e.message) ? e.message : 'A Firestore index is required for this query.'
        } else {
          error.value = (e && e.message) ? e.message : 'Failed to load podcast data.'
        }
        items.value = []
      } finally {
        loading.value = false
      }
    }

    const stats = computed(() => {
      const list = items.value
      const n = list.length || 1
      const totalCost = list.reduce((s, i) => s + i.ttsCostUsd, 0)
      const totalChars = list.reduce((s, i) => s + i.audioChars, 0)
      const dialogueCount = list.filter(i => i.audioEngine === 'dialogue').length
      return {
        totalCost,
        avgCost: totalCost / n,
        totalChars,
        avgChars: totalChars / n,
        avgTotalMs: list.reduce((s, i) => s + i.totalMs, 0) / n,
        avgTransitMs: list.reduce((s, i) => s + i.transitMs, 0) / n,
        avgTextGenMs: list.reduce((s, i) => s + i.textGenMs, 0) / n,
        avgAudioSynthMs: list.reduce((s, i) => s + i.audioSynthMs, 0) / n,
        distinctUsers: new Set(list.map(i => i.userId).filter(Boolean)).size,
        dialoguePct: list.length ? Math.round((dialogueCount / list.length) * 100) : 0
      }
    })

    // Effective TTS rate: the most common ttsRatePer1kChars among billed docs.
    const rateLabel = computed(() => {
      const counts = {}
      items.value.forEach(i => {
        if (i.ttsRatePer1kChars > 0) counts[i.ttsRatePer1kChars] = (counts[i.ttsRatePer1kChars] || 0) + 1
      })
      const rates = Object.keys(counts)
      if (!rates.length) return '$0.0000'
      if (rates.length > 1) {
        const nums = rates.map(Number).sort((a, b) => a - b)
        return `$${nums[0].toFixed(4)}–$${nums[nums.length - 1].toFixed(4)} (varies)`
      }
      return `$${Number(rates[0]).toFixed(4)}`
    })

    // date -> { count, cost, synthSum, synthCount }, sorted ascending by date.
    const daySeries = computed(() => {
      const map = {}
      items.value.forEach(i => {
        const key = i.date || '—'
        if (!map[key]) map[key] = { count: 0, cost: 0, synthSum: 0, synthCount: 0 }
        map[key].count += 1
        map[key].cost += i.ttsCostUsd
        if (i.audioSynthMs > 0) { map[key].synthSum += i.audioSynthMs; map[key].synthCount += 1 }
      })
      return Object.keys(map).sort().map(date => ({
        date,
        count: map[date].count,
        cost: map[date].cost,
        avgSynth: map[date].synthCount ? map[date].synthSum / map[date].synthCount : 0
      }))
    })

    const engineOptions = computed(() => {
      const opts = [{ value: 'all', label: 'All engines' }]
      const set = new Set(items.value.map(i => i.audioEngine).filter(Boolean))
      Array.from(set).sort().forEach(e => opts.push({ value: e, label: engineLabel(e) }))
      return opts
    })

    const filteredItems = computed(() => {
      const q = searchQuery.value.toLowerCase().trim()
      let data = items.value.filter(i => {
        if (engineFilter.value !== 'all' && i.audioEngine !== engineFilter.value) return false
        if (q) {
          const hay = `${i.userId} ${i.readingId} ${i.date} ${i.language}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
      data = [...data].sort((a, b) => {
        switch (sortKey.value) {
          case 'date-asc': return (a.timestampMs || 0) - (b.timestampMs || 0)
          case 'cost-desc': return b.ttsCostUsd - a.ttsCostUsd
          case 'synth-desc': return b.audioSynthMs - a.audioSynthMs
          case 'total-desc': return b.totalMs - a.totalMs
          default: return (b.timestampMs || 0) - (a.timestampMs || 0)
        }
      })
      return data
    })

    const destroyCharts = () => {
      [costChart, countChart, synthChart].forEach(c => c && c.destroy())
      costChart = countChart = synthChart = null
    }

    const baseScales = {
      x: { grid: { display: false }, ticks: { color: '#8b8b8b', font: { size: 11 }, maxRotation: 0, autoSkip: true } },
      y: { beginAtZero: true, grid: { color: '#2a2a2a' }, ticks: { color: '#8b8b8b', font: { size: 11 } } }
    }
    const tooltipDark = {
      backgroundColor: '#1a1b2e', titleColor: '#fff', bodyColor: '#a9a9b3',
      borderColor: '#2c2d42', borderWidth: 1, padding: 12
    }

    const renderCharts = () => {
      destroyCharts()
      const series = daySeries.value
      if (!series.length) return
      const labels = series.map(d => d.date)

      if (costCanvas.value) {
        costChart = new Chart(costCanvas.value.getContext('2d'), {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              label: 'Cost (USD)',
              data: series.map(d => +d.cost.toFixed(6)),
              backgroundColor: 'rgba(108,99,255,0.65)',
              borderColor: '#6c63ff', borderWidth: 1, borderRadius: 4
            }]
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { ...tooltipDark, callbacks: { label: c => ` $${(c.parsed.y || 0).toFixed(4)}` } }
            },
            scales: { ...baseScales, y: { ...baseScales.y, ticks: { ...baseScales.y.ticks, callback: v => '$' + v } } }
          }
        })
      }

      if (countCanvas.value) {
        countChart = new Chart(countCanvas.value.getContext('2d'), {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              label: 'Podcasts',
              data: series.map(d => d.count),
              backgroundColor: 'rgba(74,222,128,0.6)',
              borderColor: '#4ade80', borderWidth: 1, borderRadius: 4
            }]
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { ...tooltipDark, callbacks: { label: c => ` ${c.parsed.y} podcast${c.parsed.y === 1 ? '' : 's'}` } }
            },
            scales: { ...baseScales, y: { ...baseScales.y, ticks: { ...baseScales.y.ticks, precision: 0 } } }
          }
        })
      }

      if (synthCanvas.value) {
        synthChart = new Chart(synthCanvas.value.getContext('2d'), {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Avg synth (ms)',
              data: series.map(d => Math.round(d.avgSynth)),
              borderColor: '#f8bf71',
              backgroundColor: 'rgba(248,191,113,0.15)',
              borderWidth: 2.5, fill: true, tension: 0.25,
              pointRadius: 3, pointHoverRadius: 6, pointBackgroundColor: '#f8bf71'
            }]
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { ...tooltipDark, callbacks: { label: c => ` ${formatMs(c.parsed.y)}` } }
            },
            scales: { ...baseScales, y: { ...baseScales.y, ticks: { ...baseScales.y.ticks, callback: v => (v >= 1000 ? (v / 1000) + 's' : v + 'ms') } } }
          }
        })
      }
    }

    const engineLabel = e => ENGINE_LABELS[e] || e || '—'
    const truncate = (t, max) => !t ? '-' : (t.length > max ? t.slice(0, max) + '…' : t)
    const formatCost = n => '$' + (n || 0).toFixed(n && n < 0.01 ? 6 : 4)
    const formatTokens = n => {
      const v = n || 0
      if (v < 1000) return String(v)
      if (v < 1e6) return (v / 1e3).toFixed(1) + 'K'
      return (v / 1e6).toFixed(2) + 'M'
    }
    const formatMs = ms => {
      const v = Math.round(ms || 0)
      if (v < 1000) return v + 'ms'
      if (v < 60000) return (v / 1000).toFixed(1) + 's'
      const m = Math.floor(v / 60000)
      const s = Math.round((v % 60000) / 1000)
      return s ? `${m}m ${s}s` : `${m}m`
    }
    const formatDate = ms => {
      if (!ms) return '—'
      return new Date(ms).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }
    const openUser = (uid, event) => {
      if (event.metaKey || event.ctrlKey) window.open(`/user/${uid}`, '_blank')
      else router.push(`/user/${uid}`)
    }

    const rangeLabel = computed(() => `${startDate.value} → ${endDate.value}`)

    // Redraw after the DOM is patched (incl. panels that mount when data arrives).
    watch(items, () => renderCharts(), { flush: 'post' })

    onMounted(loadData)
    onBeforeUnmount(destroyCharts)

    return {
      startDate, endDate, loading, error, truncated, items,
      searchQuery, engineFilter, sortKey, columns,
      costCanvas, countCanvas, synthCanvas,
      stats, rateLabel, engineOptions, filteredItems, rangeLabel,
      expandedId, detailState, toggleRow, parseTurn,
      setRange, loadData, engineLabel, truncate, formatCost, formatTokens, formatMs, formatDate, openUser
    }
  }
}
</script>

<style scoped>
/* Header actions (page-specific: date range controls) */
.date-field {
  font-size: 12px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 6px;
}
.date-field input {
  padding: 7px 10px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: 13px;
  color-scheme: dark;
}
.quick-range { display: flex; gap: 4px; }
.quick-range button {
  padding: 6px 12px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  color: var(--text-secondary);
  font-size: 12.5px;
  cursor: pointer;
  transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}
.quick-range button:hover { color: var(--text-primary); }
.refresh-btn {
  padding: 8px 14px;
  background: var(--primary-color);
  border: none;
  border-radius: var(--radius-sm);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}
.refresh-btn:disabled { opacity: 0.5; cursor: default; }

.error-banner {
  background: rgba(248, 191, 113, 0.12);
  border: 1px solid rgba(248, 191, 113, 0.35);
  color: #f8bf71;
  border-radius: var(--radius-sm);
  padding: 10px 14px;
  font-size: 13px;
}

/* Page-specific note + inline code (also styles slotted <code> in empty state) */
.note {
  margin: 0;
  font-size: 12px;
  color: var(--text-secondary);
  opacity: 0.85;
}
code {
  font-size: 11px;
  background: rgba(255, 255, 255, 0.06);
  padding: 1px 5px;
  border-radius: 4px;
}

/* Chart panels (page-specific sizing) */
.dual-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px;
}
.chart-box { height: 300px; position: relative; }
.chart-box.small { height: 260px; }

/* Filters row */
.filters {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}
.order-box select {
  padding: 8px 12px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
}

/* Table cell content (rendered inside data-table slots) */
.muted { color: var(--text-secondary); font-weight: 400; }
.strong { font-weight: 600; }
.date-cell { white-space: nowrap; }
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link:hover { text-decoration: underline; }

.expand-caret {
  display: inline-block;
  width: 12px;
  color: var(--text-secondary);
  transition: transform 0.18s ease;
  transform: rotate(0deg);
}
.expand-caret.open { transform: rotate(90deg); color: var(--primary-color); }

/* Expanded reading detail (audio + script) */
.reading-detail { padding: 16px 18px 20px; }
.rd-msg { color: var(--text-secondary); font-size: 13px; padding: 6px 0; }
.rd-error { color: var(--warning); }
.rd-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 12px; }
.rd-titles { min-width: 0; }
.rd-title { font-size: 16px; font-weight: 700; color: var(--text-primary); }
.rd-sub { font-size: 13px; color: var(--text-secondary); margin-top: 2px; }
.rd-audio { height: 38px; width: 340px; max-width: 100%; }
.rd-summary {
  margin: 0 0 14px;
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--text-secondary);
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
}
/* Chat-bubble transcript */
.rd-chat { display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px; max-width: 900px; }
.bubble-row { display: flex; gap: 10px; align-items: flex-start; }
.avatar {
  flex-shrink: 0;
  width: 30px; height: 30px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: #fff;
  margin-top: 2px;
}
.sp-host .avatar { background: var(--primary-color); }
.sp-cohost .avatar { background: var(--info); }
.bubble {
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: 12px;
  border-top-left-radius: 3px;
  padding: 8px 13px;
}
.sp-host .bubble { border-left: 2px solid rgba(var(--primary-rgb), 0.55); }
.sp-cohost .bubble { border-left: 2px solid rgba(34, 211, 238, 0.55); }
.bubble-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 3px; }
.sp-name { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700; }
.sp-host .sp-name { color: var(--primary-color); }
.sp-cohost .sp-name { color: var(--info); }
.emotion { font-size: 10.5px; color: var(--text-secondary); font-style: italic; background: rgba(255, 255, 255, 0.06); padding: 1px 8px; border-radius: 8px; }
.bubble-text { font-size: 13.5px; line-height: 1.55; color: var(--text-primary); }

.rd-qs { border-top: 1px solid var(--border-soft); padding-top: 14px; }
.rd-qs-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); margin-bottom: 10px; }
.q-chips { display: flex; flex-direction: column; gap: 6px; }
.q-chip {
  font-size: 13px;
  color: var(--text-secondary);
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: 10px;
  padding: 7px 12px;
}
</style>
